import { UsePowerLimitsProps, UsePowerLimitsReturn } from '../types';
import { getPowerLimits } from '../utils';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook for managing dynamic power limits in PowerProfileChart
 *
 * Features:
 * - Initializes limits based on chart data
 * - Expands limits gradually when painting exceeds range
 * - Resets limits to actual data values after paint operations
 *
 * @param chartData - Array of power profile data points
 * @returns Object with current limits and update functions
 */
export const usePowerLimits = ({ chartData }: UsePowerLimitsProps): UsePowerLimitsReturn => {
  // Current dynamic power limits for Y-axis scaling
  const [dynamicPowerLimits, setDynamicPowerLimits] = useState<{ minPower: number; maxPower: number } | null>(null);

  // Ref to track if limits have been initialized (prevents re-initialization)
  const limitsInitializedRef = useRef(false);

  // Track the data range to detect when chart data changes significantly
  const previousDataRangeRef = useRef<{ min: number; max: number } | null>(null);

  /**
   * Initialize dynamic power limits when chart data is available or changes significantly
   * This prevents the limits from being recalculated on every render, but allows
   * recalculation when the data range changes (e.g., user changes input values)
   */
  useEffect(() => {
    if (chartData.length === 0) {
      return;
    }

    // Calculate current data range
    const { minPower, maxPower } = getPowerLimits(chartData);

    // Check if data range changed (comparing with previous values)
    const dataRangeChanged =
      !previousDataRangeRef.current ||
      previousDataRangeRef.current.min !== minPower ||
      previousDataRangeRef.current.max !== maxPower;

    // Only recalculate if data changed
    if (dataRangeChanged) {
      // If all points have same value, expand range to allow editing
      if (minPower === maxPower) {
        const baseValue = minPower;
        setDynamicPowerLimits({
          minPower: 0, // Always start Y-axis from 0 kW
          maxPower: baseValue + 100,
        });
      } else {
        // Always start Y-axis from 0 kW, regardless of data minimum
        setDynamicPowerLimits({
          minPower: 0, // Always start Y-axis from 0 kW
          maxPower: maxPower, // Exact maximum (peak power from input)
        });
      }

      // Update tracking refs
      limitsInitializedRef.current = true;
      previousDataRangeRef.current = { min: minPower, max: maxPower };
    }
  }, [chartData]);

  /**
   * Updates dynamic power limits when painting exceeds current range
   *
   * Behavior:
   * - If new value > maxPower: expands upper limit by a larger increment
   * - If new value < 0: prevents negative values (Y-axis always starts at 0 kW)
   * - If within range: no change
   * - minPower is always kept at 0 kW to ensure Y-axis baseline is consistent
   *
   * @param newPowerValue - The new power value
   */
  const updateDynamicPowerLimits = useCallback(
    (newPowerValue: number) => {
      if (!dynamicPowerLimits) {
        return;
      }

      const { maxPower } = dynamicPowerLimits;

      if (newPowerValue > maxPower) {
        // Expand upper limit when exceeding current maximum
        // Use larger expansion to reduce frequency of updates during painting
        setDynamicPowerLimits((prev) => {
          if (!prev) {
            return { minPower: 0, maxPower };
          }
          const exceedAmount = newPowerValue - prev.maxPower;
          const expansion = Math.max(10, Math.ceil(exceedAmount) + 5); // At least 10 kW expansion
          return {
            minPower: 0, // Always keep Y-axis starting at 0 kW
            maxPower: prev.maxPower + expansion,
          };
        });
      } else if (newPowerValue < 0) {
        // Prevent negative values - Y-axis should never go below 0 kW
        setDynamicPowerLimits((prev) => {
          if (!prev) {
            return { minPower: 0, maxPower };
          }
          return {
            minPower: 0, // Always keep Y-axis starting at 0 kW
            maxPower: prev.maxPower,
          };
        });
      }
      // Removed shrinking logic to prevent Y-axis instability during paint operations
    },
    [dynamicPowerLimits]
  );

  /**
   * Resets power limits to match actual data values with margin
   *
   * Called after paint operations complete to ensure Y-axis
   * scales properly to the actual data range, while maintaining
   * margin for future edits
   */
  const resetLimits = useCallback(() => {
    limitsInitializedRef.current = false;
    if (chartData.length > 0) {
      // Recalculate limits from current data
      const { minPower, maxPower } = getPowerLimits(chartData);

      // Always start Y-axis from 0 kW
      if (minPower === maxPower) {
        const baseValue = minPower;
        setDynamicPowerLimits({
          minPower: 0, // Always start Y-axis from 0 kW
          maxPower: baseValue + 100,
        });
      } else {
        setDynamicPowerLimits({
          minPower: 0, // Always start Y-axis from 0 kW
          maxPower: maxPower, // Exact maximum
        });
      }
    }
    limitsInitializedRef.current = true;
  }, [chartData]);

  return {
    dynamicPowerLimits,
    updateDynamicPowerLimits,
    resetLimits,
  };
};
